import React from 'react';
import { MountainIcon, Loader2Icon, AlertTriangleIcon, CheckCircle2Icon, RulerIcon, XIcon } from 'lucide-react';
import type { TerrainProfile as TerrainProfileData } from '../utils/elevation';
interface TerrainProfileProps {
  fromId: string;
  toId: string;
  profile: TerrainProfileData | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
}
const WIDTH = 1000;
const HEIGHT = 220;
const PAD_TOP = 18;
const PAD_BOTTOM = 26;
export function TerrainProfile({
  fromId,
  toId,
  profile,
  loading,
  error,
  onClose
}: TerrainProfileProps) {
  return <section className="glass-panel rounded-2xl p-4 md:p-5" data-id="element-509" data-mp="2avf4kbid3t7t">
      <header className="flex flex-wrap items-center gap-3 mb-4" data-id="element-510" data-mp="2awyl837xk6a4">
        <div className="p-1.5 rounded-lg bg-slate-700/60 border border-slate-600/60 text-slate-200" data-id="element-511" data-mp="263vz2g5hfz3y">
          <MountainIcon size={15} data-id="element-512" data-mp="1ieb5z4fm7lgy" />
        </div>

        <div className="flex flex-col leading-tight min-w-0" data-id="element-513" data-mp="1id03r91tilry">
          <h3 className="text-xs font-semibold text-slate-200 uppercase tracking-[0.12em]" data-id="element-514" data-mp="1ed9e58o12bp0">
            Profil Dataran
          </h3>
          <span className="text-[11px] font-mono text-slate-500 truncate" data-id="element-515" data-mp="134xcf42pbfiw">
            {fromId} → {toId}
          </span>
        </div>

        {profile && <div className="flex flex-wrap items-center gap-2 ml-auto" data-id="element-516" data-mp="1cxw6v2kvq0af">
            <Stat label="Jarak" value={`${profile.totalDistance.toFixed(2)} km`} icon={<RulerIcon size={11} data-id="element-518" data-mp="k8yxjoynbdtt" />} data-id="element-517" data-mp="1hvxm1cf6pfgm" />
            <Stat label="Dataran tertinggi" value={`${Math.round(profile.maxElevation.elevation)} m`} accent data-id="element-519" data-mp="ka9xtesvu3g6" />
            <Stat label="Terendah" value={`${Math.round(profile.minElevation.elevation)} m`} data-id="element-520" data-mp="u4ihdczxqm27" />
          </div>}

        <button onClick={onClose} aria-label="Tutup profil dataran" className="p-1.5 rounded-lg text-slate-500 hover:text-red-300 hover:bg-red-500/15 transition-colors flex-shrink-0" data-id="element-521" data-mp="25lryhsvld20c">
          <XIcon size={14} data-id="element-522" data-mp="agslc5ydkdf9" />
        </button>
      </header>

      {loading && <div className="h-[220px] flex items-center justify-center gap-2.5 text-xs text-slate-400 font-mono" data-id="element-523" data-mp="5jscxoe1yykz">
          <Loader2Icon size={14} className="animate-spin text-cyan-400" data-id="element-524" data-mp="jhqs2w076z1j" />
          Memuat ketinggian dataran…
        </div>}

      {!loading && error && <div className="h-[220px] flex items-center justify-center text-xs text-red-300" data-id="element-525" data-mp="3nwqkohh36vpp">
          {error}
        </div>}

      {!loading && !error && profile && <ProfileChart profile={profile} data-id="element-526" data-mp="3izqd224l9kwh" />}

      {!loading && !error && profile && <div className={`mt-3 flex items-start gap-2 px-3 py-2.5 rounded-lg text-xs border ${profile.obstructed ? 'bg-red-500/10 border-red-500/30 text-red-200' : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200'}`} data-id="element-527" data-mp="lh51j7biy5e">
          {profile.obstructed ? <AlertTriangleIcon size={14} className="mt-0.5 flex-shrink-0" data-id="element-528" data-mp="305l23ujrhpbp" /> : <CheckCircle2Icon size={14} className="mt-0.5 flex-shrink-0" data-id="element-529" data-mp="2q1mivywuwogc" />}
          <span data-id="element-530" data-mp="2uvigv1d4ebbi">
            {profile.obstructed ? <>
                Dataran memotong garis pandang setinggi{' '}
                <b data-id="element-531" data-mp="2l44iwy0thjkh">{Math.round(profile.worstClearance)} m</b> pada km{' '}
                <b data-id="element-532" data-mp="2l21jxohosg3m">{profile.maxElevation.distance.toFixed(1)}</b> — perlu
                tambahan tinggi antena atau repeater.
              </> : <>
                Garis pandang bebas hambatan. Jarak bebas terdekat{' '}
                <b data-id="element-533" data-mp="2yur7vygrzv3d">{Math.abs(Math.round(profile.worstClearance))} m</b> di atas
                puncak dataran.
              </>}
          </span>
        </div>}
    </section>;
}
function Stat({
  label,
  value,
  accent,
  icon
}: {
  label: string;
  value: string;
  accent?: boolean;
  icon?: React.ReactNode;
}) {
  return <div className="px-2.5 py-1.5 rounded-lg bg-slate-900/70 border border-slate-700/70" data-id="element-534" data-mp="1sdm9lf9i2lym">
      <div className="text-[9px] text-slate-500 uppercase tracking-[0.12em] font-semibold flex items-center gap-1" data-id="element-535" data-mp="28oy2uy3kd926">
        {icon}
        {label}
      </div>
      <div className={`text-xs font-mono font-bold ${accent ? 'text-cyan-300' : 'text-slate-200'}`} data-id="element-536" data-mp="1yy3ftrihu5fo">
        {value}
      </div>
    </div>;
}
function ProfileChart({
  profile
}: {
  profile: TerrainProfileData;
}) {
  const {
    samples,
    totalDistance,
    maxElevation,
    minElevation
  } = profile;
  const top = Math.max(maxElevation.elevation, 10) * 1.15;
  const bottom = Math.min(minElevation.elevation, 0);
  const span = Math.max(top - bottom, 1);
  const x = (distance: number) => distance / (totalDistance || 1) * WIDTH;
  const y = (elevation: number) => PAD_TOP + (1 - (elevation - bottom) / span) * (HEIGHT - PAD_TOP - PAD_BOTTOM);
  const terrainPath = samples.map((s, i) => `${i === 0 ? 'M' : 'L'} ${x(s.distance)} ${y(s.elevation)}`).join(' ');
  const areaPath = `${terrainPath} L ${WIDTH} ${HEIGHT - PAD_BOTTOM} L 0 ${HEIGHT - PAD_BOTTOM} Z`;
  const startY = y(samples[0].elevation);
  const endY = y(samples[samples.length - 1].elevation);
  const gridLines = [0, 0.25, 0.5, 0.75, 1].map(ratio => ({
    ratio,
    value: Math.round(bottom + span * (1 - ratio)),
    y: PAD_TOP + ratio * (HEIGHT - PAD_TOP - PAD_BOTTOM)
  }));
  return <div className="relative" data-id="element-537" data-mp="g01nrefvd07">
      <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className="w-full h-[220px]" role="img" aria-label="Profil ketinggian dataran antara dua site" preserveAspectRatio="none" data-id="element-538" data-mp="eymbaqe44co">
        {gridLines.map(line => <g key={line.ratio} data-id="element-539" data-mp="efj5i807w26p">
            <line x1={0} x2={WIDTH} y1={line.y} y2={line.y} stroke="rgba(148,163,184,0.14)" strokeWidth={1} data-id="element-540" data-mp="edoh2jpqlrxv" />
            <text x={6} y={line.y - 4} fill="#64748b" fontSize={11} fontFamily="ui-monospace, monospace" data-id="element-541" data-mp="2g6w7ao6w1tyj">
              {line.value} m
            </text>
          </g>)}

        {/* Terrain */}
        <path d={areaPath} fill="rgba(16,185,129,0.16)" data-id="element-542" data-mp="2i4nlhwnu8iyq" />
        <path d={terrainPath} fill="none" stroke="#34d399" strokeWidth={2} vectorEffect="non-scaling-stroke" data-id="element-543" data-mp="2d5173jw9wwoc" />

        {/* Line of sight */}
        <line x1={0} y1={startY} x2={WIDTH} y2={endY} stroke={profile.obstructed ? '#f87171' : '#38bdf8'} strokeWidth={2} strokeDasharray="8 6" vectorEffect="non-scaling-stroke" data-id="element-544" data-mp="2n377fx2flur3" />

        {/* Highest terrain marker */}
        <line x1={x(maxElevation.distance)} y1={y(maxElevation.elevation)} x2={x(maxElevation.distance)} y2={HEIGHT - PAD_BOTTOM} stroke="rgba(226,232,240,0.4)" strokeWidth={1} strokeDasharray="3 4" data-id="element-545" data-mp="3as07o5q36ori" />
        <circle cx={x(maxElevation.distance)} cy={y(maxElevation.elevation)} r={4} fill="#e2e8f0" data-id="element-546" data-mp="3tgtwiu3r7ceq" />

        {/* Endpoints */}
        <circle cx={2} cy={startY} r={4} fill="#38bdf8" data-id="element-547" data-mp="3fgsydmevij2y" />
        <circle cx={WIDTH - 2} cy={endY} r={4} fill="#38bdf8" data-id="element-548" data-mp="3kfcj5jhqdotq" />
      </svg>

      <div className="flex items-center justify-between mt-1 text-[10px] font-mono text-slate-500" data-id="element-549" data-mp="35rhqu47mxmh6">
        <span data-id="element-550" data-mp="3ahprrxj8w4sv">0 km</span>
        <span className="text-slate-300" data-id="element-551" data-mp="3aizrer5qxl1m">
          Puncak {Math.round(maxElevation.elevation)} m · km{' '}
          {maxElevation.distance.toFixed(1)}
        </span>
        <span data-id="element-552" data-mp="31ped18xhu5sq">{totalDistance.toFixed(1)} km</span>
      </div>

      <div className="flex flex-wrap items-center gap-4 mt-2.5 pt-2.5 border-t border-slate-800/70" data-id="element-553" data-mp="31pw6xywoo7xh">
        <Legend color="#34d399" label="Permukaan dataran" data-id="element-554" data-mp="2mt5ds7dlrsgb" />
        <Legend color={profile.obstructed ? '#f87171' : '#38bdf8'} label="Garis pandang A → B" dashed data-id="element-555" data-mp="2rrq67kmm57u7" />
        <Legend color="#e2e8f0" label="Titik tertinggi" data-id="element-556" data-mp="377do3chk63lp" />
      </div>
    </div>;
}
function Legend({
  color,
  label,
  dashed
}: {
  color: string;
  label: string;
  dashed?: boolean;
}) {
  return <div className="flex items-center gap-2" data-id="element-557" data-mp="2yaw9fz9zjnl7">
      <span className="h-[2px] w-6 flex-shrink-0" style={dashed ? {
      backgroundImage: `repeating-linear-gradient(90deg, ${color} 0 5px, transparent 5px 9px)`
    } : {
      background: color
    }} data-id="element-558" data-mp="2tai1sicaqfnp" />
      <span className="text-[10px] text-slate-400" data-id="element-559" data-mp="2fi23fuucb5t4">{label}</span>
    </div>;
}